/* eslint-disable */
// ============================================================
//  push.js — Web Push (notifications système : bannière + pastille)
//
//  Côté navigateur :
//   - demande la permission + s'abonne via PushManager (clé VAPID)
//   - enregistre l'abonnement dans Supabase (table push_subscriptions)
//   - gère la pastille sur l'icône via l'API Badging (setAppBadge)
//
//  L'envoi réel des pushs se fait côté serveur (Edge Function Supabase
//  `send-push`, déclenchée à l'insert d'une ligne `notifications`).
// ============================================================
import { supabase } from "./supabase";

// Clé publique VAPID (la privée vit côté Edge Function, jamais ici).
export const VAPID_PUBLIC_KEY =
  "BLw0v-dvBQQWq18c9Y_9ezjRd_3KCIYqUyk5REeRkCYUP0KOjnNr5NmtkUF_bD_kUaQV8NO8ltCKeY6KX0qL4Sk";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Permission courante : "granted" | "denied" | "default" | "unsupported"
export function pushPermission() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Course avec un timeout : en TWA/WebView, serviceWorker.ready ou subscribe()
// peuvent ne JAMAIS résoudre -> sans ça, le toggle se bloque sans aucun message.
function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout:" + label)), ms)),
  ]);
}

// Active les notifications : permission -> abonnement push -> enregistrement en base.
// Retourne true si abonné, sinon throw avec un message exploitable côté UI.
export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");

  const perm = await withTimeout(Notification.requestPermission(), 30000, "permission");
  if (perm !== "granted") throw new Error("denied");

  const reg = await withTimeout(navigator.serviceWorker.ready, 12000, "sw");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await withTimeout(reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }), 20000, "subscribe");
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("not_logged_in");

  const json = sub.toJSON();
  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      user_id: user.id,
      endpoint: json.endpoint,
      p256dh: json.keys && json.keys.p256dh,
      auth: json.keys && json.keys.auth,
      user_agent: navigator.userAgent.slice(0, 250),
    },
    { onConflict: "endpoint" }
  );
  if (error) throw error;
  return true;
}

// Désactive : supprime l'abonnement (base + navigateur).
export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
      await sub.unsubscribe();
    }
  } catch (e) {
    /* best effort */
  }
  clearBadge();
}

// Indique si CE navigateur est déjà abonné (permission accordée + souscription active).
export async function isSubscribed() {
  if (!pushSupported() || Notification.permission !== "granted") return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch (e) {
    return false;
  }
}

// === Pastille sur l'icône (API Badging) ===
export function setBadge(n) {
  try {
    if ("setAppBadge" in navigator) {
      if (n > 0) navigator.setAppBadge(n);
      else navigator.clearAppBadge();
    }
  } catch (e) {}
}
export function clearBadge() {
  try {
    if ("clearAppBadge" in navigator) navigator.clearAppBadge();
  } catch (e) {}
}
